import Keycloak from 'keycloak-js';

class AuthService {
    private keycloak: Keycloak;
    private initialized: boolean = false;
    private guestMode: boolean = false;
    private guestToken: string | null = null;
    private guestUser: any = null;

    constructor() {
        this.keycloak = new Keycloak({
            url: 'https://login.gjamerson.com',
            realm: 'migoyugo',
            clientId: 'migoyugo-app'
        });
    }

    async testConnection(): Promise<boolean> {
        try {
            const response = await fetch('https://login.gjamerson.com/realms/migoyugo/.well-known/openid-configuration', {
                method: 'GET',
                mode: 'cors'
            });
            
            if (response.ok) {
                console.log('✓ Keycloak server is reachable');
                const config = await response.json();
                console.log('✓ Realm configuration loaded:', config.issuer);
                return true;
            } else {
                console.error('✗ Keycloak server returned status:', response.status);
                return false;
            }
        } catch (error) {
            console.error('✗ Cannot reach Keycloak server:', error);
            return false;
        }
    }

    async init(): Promise<boolean> {
        if (this.initialized) {
            return this.keycloak.authenticated || this.guestMode;
        }

        // First check if we have a stored guest session
        const storedToken = localStorage.getItem('guestToken');
        const storedUser = localStorage.getItem('guestUser');
        
        if (storedToken && storedUser) {
            try {
                this.guestMode = true;
                this.guestToken = storedToken;
                this.guestUser = JSON.parse(storedUser);
                this.initialized = true;
                console.log('Restored guest session for:', this.guestUser.username);
                return true;
            } catch (error) {
                console.error('Failed to restore guest session:', error);
                // Clear invalid stored data
                localStorage.removeItem('guestToken');
                localStorage.removeItem('guestUser');
            }
        }

        try {
            // If no guest session, try to check if Keycloak is reachable
            console.log('Checking Keycloak availability...');
            const isReachable = await this.testConnection();
            
            if (!isReachable) {
                console.warn('Keycloak server is not reachable. Running in offline mode.');
                return false;
            }
            
            console.log('Initializing Keycloak with:', {
                url: 'https://login.gjamerson.com',
                realm: 'migoyugo',
                clientId: 'migoyugo-app',
                redirectUri: window.location.origin
            });
            
            const authenticated = await this.keycloak.init({
                onLoad: 'check-sso',
                silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
                pkceMethod: 'S256',
                checkLoginIframe: false,
                enableLogging: true,
                responseMode: 'query'  // Use query instead of fragment for better debugging
            });

            // Mark as initialized even if not authenticated - the Keycloak object is ready
            this.initialized = true;

            // Setup automatic token refresh
            setInterval(() => {
                this.keycloak.updateToken(30).catch(() => {
                    console.log('Failed to refresh token');
                });
            }, 60000); // Refresh every minute

            return authenticated;
        } catch (error) {
            console.warn('Keycloak initialization completed with warnings:', error);
            
            // Even if init failed, mark as initialized if server is reachable
            // This allows users to still attempt login
            if (await this.testConnection()) {
                console.log('Keycloak server is reachable, allowing login attempts');
                this.initialized = true;
            } else {
                console.error('Keycloak server is not reachable');
                this.initialized = false;
            }
            
            return false;
        }
    }

    async login(): Promise<void> {
        // Always attempt login - even if init failed, user should be able to try
        await this.keycloak.login();
    }

    async logout(): Promise<void> {
        // Handle guest logout
        if (this.guestMode) {
            this.guestMode = false;
            this.guestToken = null;
            this.guestUser = null;
            localStorage.removeItem('guestToken');
            localStorage.removeItem('guestUser');
            // Reload the page to reset the UI
            window.location.reload();
            return;
        }
        
        // Only logout if we're actually initialized and authenticated with Keycloak
        if (!this.initialized || !this.keycloak.authenticated) {
            console.log('Not logged in, skipping logout');
            return;
        }
        await this.keycloak.logout({
            redirectUri: window.location.origin
        });
    }

    async register(): Promise<void> {
        // Always attempt register - even if init failed, user should be able to try
        await this.keycloak.register();
    }

    async loginAsGuest(): Promise<boolean> {
        try {
            // Determine the correct API URL based on environment
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const apiUrl = isLocalhost ? 'http://localhost:5036/api/auth/guest' : '/api/auth/guest';
            
            // Call the guest login API endpoint
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                this.guestMode = true;
                this.guestToken = data.accessToken;
                this.guestUser = data.player;
                this.initialized = true;
                
                // Store guest session in localStorage
                localStorage.setItem('guestToken', data.accessToken);
                localStorage.setItem('guestUser', JSON.stringify(data.player));
                
                console.log('Logged in as guest:', data.player.username);
                return true;
            } else {
                console.error('Guest login failed:', response.statusText);
                return false;
            }
        } catch (error) {
            console.error('Guest login error:', error);
            return false;
        }
    }

    getToken(): string | undefined {
        if (this.guestMode) {
            return this.guestToken || undefined;
        }
        return this.keycloak.token;
    }

    getTokenParsed(): any {
        if (this.guestMode) {
            return { 
                sub: this.guestUser?.id,
                preferred_username: this.guestUser?.username
            };
        }
        return this.keycloak.tokenParsed;
    }

    isAuthenticated(): boolean {
        if (this.guestMode) {
            return !!this.guestToken;
        }
        return this.keycloak.authenticated || false;
    }

    getUsername(): string | undefined {
        if (this.guestMode) {
            return this.guestUser?.username;
        }
        return this.keycloak.tokenParsed?.preferred_username;
    }

    getUserId(): string | undefined {
        if (this.guestMode) {
            return this.guestUser?.id;
        }
        return this.keycloak.tokenParsed?.sub;
    }

    isGuest(): boolean {
        return this.guestMode;
    }

    hasRole(role: string): boolean {
        return this.keycloak.hasRealmRole(role);
    }

    async updateToken(minValidity: number = 5): Promise<string | undefined> {
        try {
            const refreshed = await this.keycloak.updateToken(minValidity);
            if (refreshed) {
                console.log('Token was successfully refreshed');
            }
            return this.keycloak.token;
        } catch (error) {
            console.error('Failed to refresh token', error);
            throw error;
        }
    }
}

export const authService = new AuthService();